import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEO from "@/components/base/SEO";
import FactoryDashboardStats from "./components/FactoryDashboardStats";
import ProductListingTable from "./components/ProductListingTable";
import LeadsPanel from "./components/LeadsPanel";
import CommissionOverview from "./components/CommissionOverview";
import { mockFactoryListings, mockFactoryLeads, mockFactoryStats, FactoryListing, FactoryLead } from "@/mocks/factoryDashboard";

type Tab = "overview" | "listings" | "leads" | "commission";

const tabs: { value: Tab; label: string; icon: string }[] = [
  { value: "overview", label: "Overview", icon: "ri-dashboard-line" },
  { value: "listings", label: "Product Listings", icon: "ri-store-2-line" },
  { value: "leads", label: "Buyer Leads", icon: "ri-mail-open-line" },
  { value: "commission", label: "Commission", icon: "ri-percent-line" },
];

export default function FactoryDashboardPage() {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [listings, setListings] = useState<FactoryListing[]>(mockFactoryListings);
  const [leads, setLeads] = useState<FactoryLead[]>(mockFactoryLeads);

  const handleUpdateCommission = (id: string, commission: number) => {
    setListings((prev) => prev.map((l) => (l.id === id ? { ...l, commission } : l)));
  };

  const handleToggleStatus = (id: string) => {
    setListings((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const next = l.status === "active" ? "paused" : "active";
        return { ...l, status: next };
      })
    );
  };

  const handleUpdateLeadStatus = (id: string, status: FactoryLead["status"]) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const avgCommission = Math.round(
    listings.reduce((sum, l) => sum + l.commission, 0) / listings.length * 10
  ) / 10;

  const newLeadsCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f7f7f5" }}>
      <SEO
        title="Factory Seller Dashboard — StarBookPrime"
        description="Manage your factory product listings, set commission rates, and view incoming buyer requirement leads."
        canonical="/factory-dashboard"
        noIndex={true}
      />
      <Navbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg">
                  <i className="ri-building-2-line text-white text-base" />
                </div>
                <span className="text-xs font-semibold text-orange-500 uppercase tracking-wide">Factory Seller</span>
              </div>
              <h1 className="text-2xl font-extrabold text-gray-900">Factory Dashboard</h1>
              <p className="text-sm text-gray-500 mt-1">TechVision Factory · Austin, TX · <span className="text-green-600 font-medium">Verified</span> <i className="ri-shield-check-fill text-green-500" /></p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/upload"
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" /> Add Product
              </Link>
              <Link
                to="/factory/techvision"
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line" /> View Store
              </Link>
            </div>
          </div>

          {/* Stats */}
          <FactoryDashboardStats />

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 bg-white rounded-xl border border-gray-100 p-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${activeTab === tab.value ? "bg-orange-500 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              >
                <i className={tab.icon} />
                {tab.label}
                {tab.value === "leads" && newLeadsCount > 0 && (
                  <span className={`inline-flex items-center justify-center w-4 h-4 text-xs font-bold rounded-full ${activeTab === tab.value ? "bg-white/20 text-white" : "bg-teal-500 text-white"}`}>
                    {newLeadsCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <CommissionOverview avgCommission={avgCommission} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Products */}
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-base font-bold text-gray-900 mb-4">Top Performing Products</h3>
                  <div className="space-y-3">
                    {listings
                      .filter((l) => l.status === "active")
                      .sort((a, b) => b.totalRevenue - a.totalRevenue)
                      .slice(0, 3)
                      .map((l, i) => (
                        <div key={l.id} className="flex items-center gap-3">
                          <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-gray-400 bg-gray-50 rounded-full flex-shrink-0">
                            {i + 1}
                          </span>
                          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                            <img src={l.image} alt={l.title} className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{l.title}</p>
                            <p className="text-xs text-gray-400">{l.totalSales.toLocaleString()} sales</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-green-600">${(l.totalRevenue / 1000).toFixed(1)}k</p>
                            <p className="text-xs text-orange-500">{l.commission}% comm.</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Recent Leads */}
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-gray-900">Recent Buyer Leads</h3>
                    <button onClick={() => setActiveTab("leads")} className="text-xs text-orange-500 font-medium hover:underline cursor-pointer whitespace-nowrap">
                      View all
                    </button>
                  </div>
                  <div className="space-y-3">
                    {leads.slice(0, 3).map((lead) => (
                      <div key={lead.id} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50/60 border border-gray-100">
                        <span className="text-xl flex-shrink-0 mt-0.5">{lead.countryFlag}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{lead.title}</p>
                          <p className="text-xs text-gray-400">{lead.quantity} · {lead.budget}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className="text-xs font-bold text-orange-500">{lead.matchScore}%</span>
                          <p className="text-xs text-gray-400">match</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "listings" && (
            <ProductListingTable
              listings={listings}
              onUpdateCommission={handleUpdateCommission}
              onToggleStatus={handleToggleStatus}
            />
          )}

          {activeTab === "leads" && (
            <LeadsPanel leads={leads} onUpdateStatus={handleUpdateLeadStatus} />
          )}

          {activeTab === "commission" && (
            <CommissionOverview avgCommission={avgCommission} />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
